import { styled, SvgIcon } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useViewport } from 'contexts/viewport';
import { useRef } from 'react';
import Box from 'shell/components/Box';
import Button from 'shell/components/Button';
import Icon from 'shell/components/Icon';
import Stack from 'shell/components/Stack';
import Text from 'shell/components/Text';
import { getPrettyFileTypes } from 'utils/helper';

const FileUpload = ({ accept, description, error, multiple = false, value, onChange, ...rest }) => {
    const theme = useTheme();
    const { device } = useViewport();

    const dropZoneRef = useRef(null);
    const inputRef = useRef(null);

    const catchEvent = (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
    };

    const handleClick = () => {
        inputRef.current.click();
    };

    const handleChange = (e) => {
        onChange && onChange(e.target.files);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onChange && onChange(e.dataTransfer.files);
    };

    return (
        <DropArea
            ref={dropZoneRef}
            {...rest}
            onClick={handleClick}
            onDragEnter={catchEvent}
            onDragOver={catchEvent}
            onDragLeave={catchEvent}
            onDrop={handleDrop}
            onMouseUp={catchEvent}
        >
            <SvgIcon sx={{ width: '100%', height: '100%' }} viewBox={'0 0 320 112'}>
                <path
                    d={
                        'M168.098 88.4945C168.098 68.6242 141.818 65.9461 141.818 65.9461C141.818 65.9461 147.589 56.2614 135.634 47.0942C125.874 39.6108 110.384 41.0567 105.231 43.7674C98.5317 32.678 69.6746 25.6548 48.0319 33.0477C30.7176 38.962 24.8432 51.8996 24.8439 60.7711C17.6297 61.1407 -0.405978 72.9694 0.109326 88.4945C0.62463 104.02 18.145 114 32.0582 114H141.818C150.062 112.891 168.098 108.365 168.098 88.4945Z'
                    }
                    fill={theme.palette.ensightful.shades[0]}
                />
                <path
                    opacity={'0.6'}
                    d={
                        'M285.296 36.9072C285.296 24.37 269.653 22.6803 269.653 22.6803C269.653 22.6803 273.088 16.5697 265.971 10.7856C260.162 6.06394 250.942 6.97623 247.874 8.68657C243.887 1.68972 226.71 -2.74163 213.828 1.92295C203.521 5.6546 200.025 13.8176 200.025 19.4151C195.731 19.6483 184.995 27.1116 185.302 36.9072C185.609 46.7028 196.038 53 204.319 53H269.653C274.56 52.3003 285.296 49.4445 285.296 36.9072Z'
                    }
                    fill={theme.palette.ensightful.shades[0]}
                />
                <path
                    opacity={'0.9'}
                    d={
                        'M319.296 55.2836C319.296 47.714 310.066 46.6938 310.066 46.6938C310.066 46.6938 312.093 43.0043 307.894 39.5121C304.467 36.6612 299.027 37.2121 297.217 38.2447C294.865 34.0202 284.73 31.3447 277.13 34.161C271.049 36.4141 268.986 41.3427 268.986 44.7223C266.453 44.8631 260.119 49.3693 260.3 55.2836C260.481 61.1979 266.634 65 271.52 65H310.066C312.962 64.5775 319.296 62.8533 319.296 55.2836Z'
                    }
                    fill={theme.palette.ensightful.shades[0]}
                />
            </SvgIcon>
            <CustomStack data-cy={'upload-button'} alignItems={'center'} spacing={4}>
                <Stack alignItems={'center'} spacing={1}>
                    <CustomIcon kind={'upload'} />
                    <Text align={'center'} variant={'p1'}>
                        {device !== 'mobile' ? 'Drag and drop to upload' : 'Upload File'}
                    </Text>
                    {device !== 'mobile' && (
                        <Text align={'center'} variant={'p3'}>
                            or
                        </Text>
                    )}
                    <CustomButton>Click to Browse</CustomButton>
                </Stack>
                {accept && typeof accept !== 'string' && (
                    <Text align={'center'} color={'light'} variant={'p3'}>
                        Supported file types: {getPrettyFileTypes(accept)}
                    </Text>
                )}
                <Text align={'center'} color={'light'} variant={'p3'}>
                    {description}
                </Text>
                {error ? (
                    <ErrorStack alignItems={'center'} direction={'row'} spacing={2}>
                        <ErrorIcon kind={'error'} />
                        <Text color={'error'} variant={'p3'}>
                            {error}
                        </Text>
                    </ErrorStack>
                ) : null}
            </CustomStack>
            <CustomInput
                data-cy={'click-to-browse-button'}
                ref={inputRef}
                accept={
                    !accept || typeof accept === 'string'
                        ? accept
                        : accept.reduce((acc, cur, index) => {
                              return index ? `${acc},${cur}` : cur;
                          }, '')
                }
                multiple={multiple}
                type={'file'}
                value={value}
                onChange={handleChange}
            />
        </DropArea>
    );
};

const DropArea = styled(Box)(({ theme }) => {
    return {
        position: 'relative',
        border: `1px dashed ${theme.palette.ensightful.neutral[500]}`,
        borderRadius: '12px',
        width: '100%',
        height: '100%',
        padding: theme.spacing(4),
        background: theme.palette.ensightful.primary[50],
        cursor: 'pointer',
        transition: theme.transitions.default,
        '&:hover': {
            borderColor: theme.palette.ensightful.primary[500],
        },
    };
});

const CustomStack = styled(Stack)(({ theme }) => {
    return {
        position: 'absolute',
        top: '50%',
        left: '50%',
        width: '100%',
        padding: theme.spacing(0, 7),
        transform: 'translate(-50%, -50%)',
    };
});

const CustomIcon = styled(Icon)(({ theme }) => {
    return {
        fontSize: '1rem',
        color: theme.palette.ensightful.primary[500],
    };
});

const CustomButton = styled(Button)(() => {
    return {
        width: 'fit-content',
    };
});

const ErrorStack = styled(Stack)(({ theme }) => {
    return {
        border: `1px solid ${theme.palette.ensightful.error[700]}`,
        borderRadius: '4px',
        padding: theme.spacing(2),
        background: theme.palette.ensightful.error[50],
    };
});

const ErrorIcon = styled(Icon)(({ theme }) => {
    return {
        borderRadius: '50%',
        padding: '6px',
        fontSize: '1.5rem',
        background: theme.palette.ensightful.error[700],
        color: theme.palette.ensightful.shades[0],
        '&:focus-visible': {
            outline: `${theme.palette.ensightful.secondary[400]} solid 3px`,
            outlineOffset: '4px',
        },
    };
});

const CustomInput = styled('input')(() => {
    return {
        display: 'none',
    };
});

export default FileUpload;
